import React, { useState, useEffect, useRef, useMemo } from 'react';

import { DonutChart } from 'react-circle-chart';
import { useTranslation } from 'react-i18next';

import GraphHoverInfo from './GraphHoverInfo';

import classes from './styles.module.scss';

export default function JobSummary({ stats = {} }) {
  const [graphTooltipData, setGraphTooltipData] = useState(null);
  const [hoverCoords, setHoverCoords] = useState(null);

  const graphRef = useRef();

  const { t } = useTranslation();

  const total =
    stats.pipelineSubmit +
    stats.pipelineInterview +
    stats.pipelineOffer +
    stats.pipelineOnboardAll;

  const graphItems = useMemo(() => {
    const items = [];
    const newShare = (100 / total) * stats.pipelineSubmit;
    if (newShare) {
      items.push({
        label: t('dashboardComponents.Jobs.MyJobs.JobSummary.pendingReview'),
        value: newShare,
        color: 'rgb(0, 227, 171)',
        integerValue: stats.pipelineSubmit,
      });
    }

    const interviewShare = (100 / total) * stats.pipelineInterview;
    if (interviewShare) {
      items.push({
        label: t('dashboardComponents.Jobs.MyJobs.JobSummary.interview'),
        value: interviewShare,
        color: 'rgb(255, 184, 0)',
        integerValue: stats.pipelineInterview,
      });
    }

    const offerShare = (100 / total) * stats.pipelineOffer;
    if (offerShare) {
      items.push({
        label: t('dashboardComponents.Jobs.MyJobs.JobSummary.offer'),
        value: offerShare,
        color: 'rgb(255, 45, 85)',
        integerValue: stats.pipelineOffer,
      });
    }

    const onboardShare = (100 / total) * stats.pipelineOnboardAll;
    if (onboardShare) {
      items.push({
        label: t('dashboardComponents.Jobs.MyJobs.JobSummary.onboard'),
        value: onboardShare,
        color: 'rgb(103, 34, 210)',
        integerValue: stats.pipelineOnboardAll,
      });
    }

    return items;
  }, [stats, total, t]);

  useEffect(() => {
    const detectGraphElementUnderCursor = (event) => {
      const hoveredGraphPart = document.elementFromPoint(
        event.clientX,
        event.clientY
      );

      setHoverCoords({
        x: event.clientX,
        y: event.clientY,
      });

      const hoveredGraphPartColor =
        getComputedStyle(hoveredGraphPart).getPropertyValue('stroke');

      if (hoveredGraphPart.tagName === 'circle') {
        const hoveredGraphPartData = graphItems.find(
          (item) => item.color === hoveredGraphPartColor
        );

        if (hoveredGraphPartData) {
          setGraphTooltipData(hoveredGraphPartData);
        }
      } else {
        setGraphTooltipData(null);
      }
    };

    graphRef.current.addEventListener(
      'mousemove',
      detectGraphElementUnderCursor
    );

    return () => {
      graphRef.current?.removeEventListener(
        'mousemove',
        detectGraphElementUnderCursor
      );
    };
  }, [graphItems]);

  return (
    <div className={classes.JobSummary}>
      <h2>
        {t('dashboardComponents.Jobs.MyJobs.JobSummary.totalHiringPipeline')}
      </h2>
      <div className={classes.data}>
        <div className={classes.graphBorder}>
          <div className={classes.total}>{total}</div>
        </div>
        <div
          className={classes.graph}
          ref={graphRef}
          onMouseLeave={() => setGraphTooltipData(null)}
        >
          <DonutChart
            size={162}
            tooltipSx={{ display: 'none' }}
            trackWidth="lg"
            showTotal={false}
            items={graphItems}
          />
        </div>
        <ul className={classes.stats}>
          <li>
            <div>
              <svg
                width="10"
                height="11"
                viewBox="0 0 10 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  y="0.5"
                  width="10"
                  height="10"
                  rx="5"
                  fill="rgb(0, 227, 171)"
                />
              </svg>
              {t('dashboardComponents.Jobs.MyJobs.JobSummary.pendingReview')}
            </div>
            <span className={classes.value}>{stats.pipelineSubmit}</span>
          </li>
          <li>
            <div>
              <svg
                width="10"
                height="11"
                viewBox="0 0 10 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect y="0.5" width="10" height="10" rx="5" fill="#FFB800" />
              </svg>
              {t('dashboardComponents.Jobs.MyJobs.JobSummary.interview')}
            </div>
            <span className={classes.value}>{stats.pipelineInterview}</span>
          </li>

          <li>
            <div>
              <svg
                width="10"
                height="11"
                viewBox="0 0 10 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect y="0.5" width="10" height="10" rx="5" fill="#FF2D55" />
              </svg>
              {t('dashboardComponents.Jobs.MyJobs.JobSummary.offer')}
            </div>
            <span className={classes.value}>{stats.pipelineOffer}</span>
          </li>
          <li>
            <div>
              <svg
                width="10"
                height="11"
                viewBox="0 0 10 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect y="0.5" width="10" height="10" rx="5" fill="#6722D2" />
              </svg>
              {t('dashboardComponents.Jobs.MyJobs.JobSummary.onboard')}
            </div>
            <span className={classes.value}>{stats.pipelineOnboardAll}</span>
          </li>
        </ul>
      </div>
      {graphTooltipData && (
        <GraphHoverInfo
          label={graphTooltipData.label}
          value={graphTooltipData.integerValue}
          coords={hoverCoords}
        />
      )}
    </div>
  );
}
